import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { connectionApi } from '../../lib/connections-api';
import { useAuth } from '../../lib/auth';
import shell from '../shell/AppShell.module.css';
export function useNavigationStatus() {
  const { session } = useAuth();
  const cache = useQueryClient();
  const scope = session?.bootstrap.workspace.id;
  const member = session?.bootstrap.user?.id;
  const query = useQuery({
    queryKey: ['navigation-status', scope, member],
    queryFn: connectionApi.navigation,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
    staleTime: 0,
  });
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const expiry = query.data ? Date.parse(query.data.validUntil) : Date.now();
    setNow(Date.now());
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.max(0, expiry - Date.now()) + 10,
    );
    return () => window.clearTimeout(timer);
  }, [query.data]);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible')
        void cache.invalidateQueries({
          queryKey: ['navigation-status', scope, member],
        });
    };
    window.addEventListener('navigation-status-changed', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('navigation-status-changed', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [cache, scope, member]);
  return {
    ...query,
    fresh: !!query.data && Date.parse(query.data.validUntil) > now,
  };
}
export function NavigationDot({
  label,
  kind,
  status,
}: {
  label: string;
  kind: 'sequences' | 'connections';
  status: ReturnType<typeof useNavigationStatus>;
}) {
  const aggregate = status.data?.[kind];
  const ready = !status.isError && status.fresh && aggregate?.ready;
  const text =
    status.isError || aggregate?.reason === 'status_unavailable'
      ? 'status unavailable'
      : status.isPending || !status.fresh
        ? 'checking'
        : ready
          ? kind === 'sequences'
            ? 'at least one active'
            : 'at least one ready'
          : kind === 'sequences'
            ? 'none active'
            : 'none ready';
  return (
    <span
      className={shell.itemCount}
      title={`${label}: ${text}`}
      aria-label={`${label}: ${text}`}
    >
      <span
        style={{
          display: 'inline-block',
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: ready ? '#2ca46e' : '#90959b',
        }}
        aria-hidden="true"
      />
    </span>
  );
}
